/**-----------------------------------------------------------------------------------------
* Copyright © 2021 Progress Software Corporation. All rights reserved.
* Licensed under commercial license. See LICENSE.md in the project root for more information
*-------------------------------------------------------------------------------------------*/
"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
var tslib_1 = require("tslib");
var core_1 = require("@angular/core");
var kendo_angular_l10n_1 = require("@progress/kendo-angular-l10n");
var kendo_angular_popup_1 = require("@progress/kendo-angular-popup");
var date_range_popup_template_directive_1 = require("./date-range-popup-template.directive");
var date_range_service_1 = require("./date-range.service");
var multiview_calendar_component_1 = require("../calendar/multiview-calendar.component");
var preventable_event_1 = require("../preventable-event");
var kendo_angular_common_1 = require("@progress/kendo-angular-common");
var rxjs_1 = require("rxjs");
var operators_1 = require("rxjs/operators");
var util_1 = require("../util");
var utils_1 = require("../common/utils");
/**
 * Represents the Kendo UI DateRangePopup component for Angular.
 *
 * @example
 * ```ts
 * import { DateInputsModule, DateRangeService } from '@progress/kendo-angular-dateinputs';
 *
 * _@Component({
 * providers: [DateRangeService],
 * selector: 'my-app',
 * template: `
 *  <button #anchor (click)="popup.toggle()">Toggle</button>
 *  <kendo-daterange-popup [anchor]="anchor" #popup></kendo-daterange-popup>
 * `
 * })
 * export class AppComponent {
 * }
 * ```
 */
var DateRangePopupComponent = /** @class */ (function () {
    function DateRangePopupComponent(popupService, dateRangeService, ref, zone, rtl) {
        this.popupService = popupService;
        this.dateRangeService = dateRangeService;
        this.ref = ref;
        this.zone = zone;
        this.rtl = rtl;
        /**
         * Controls the popup animation.
         * By default, the opening and closing animations are enabled.
         * For more information about controlling the popup animations,
         * refer to the article on [animations]({% slug animations_popup %}).
         */
        this.animate = true;
        /**
         * Configures the collision behavior of the popup.
         * For more information, refer to the article on
         * [viewport boundary detection]({% slug viewportboundarydetection_popup %}).
         */
        this.collision = { horizontal: 'fit', vertical: 'flip' };
        /**
         * Fires each time the popup is about to open.
         * This event is preventable. If you cancel the event, the popup will remain closed.
         * For more information, refer to the section on
         * [events]({% slug overview_datepicker %}#toc-events).
         */
        this.open = new core_1.EventEmitter();
        /**
         * Fires each time the popup is about to close.
         * This event is preventable. If you cancel the event, the popup will remain open.
         * For more information, refer to the section on
         * [events]({% slug overview_datepicker %}#toc-events).
         */
        this.close = new core_1.EventEmitter();
        /**
         * Fires each time the calendar element is blurred.
         */
        this.onBlur = new core_1.EventEmitter(); //tslint:disable-line:no-output-rename
        /**
         * Fires each time the calendar element is focused.
         */
        this.onFocus = new core_1.EventEmitter(); //tslint:disable-line:no-output-rename
        /**
         * Fires each time the popup is closed either on `ESC` keypress or on leaving the viewport.
         */
        this.cancel = new core_1.EventEmitter();
        /**
         * @hidden
         */
        this.popupUID = kendo_angular_common_1.guid();
        this.calendarSubscriptions = new rxjs_1.Subscription();
        this.popupSubscriptions = new rxjs_1.Subscription();
        this.resolvedPromise = Promise.resolve();
    }
    Object.defineProperty(DateRangePopupComponent.prototype, "calendar", {
        /**
         * The active calendar that is visible in the popup.
         *
         * > When the popup is closed, the property returns `null`.
         */
        get: function () {
            return this._calendar;
        },
        set: function (calendar) {
            this._calendar = calendar;
            this.subscribeFocusBlur(calendar);
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(DateRangePopupComponent.prototype, "isActive", {
        /**
         * Gets the active state of the component.
         * When the opened calendar is active, returns `true`.
         */
        get: function () {
            return this.calendar && this.calendar.isActive;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(DateRangePopupComponent.prototype, "show", {
        get: function () {
            return this._show;
        },
        /**
         * Gets or sets the visibility state of the component.
         */
        set: function (show) {
            if (this._show === show) {
                return;
            }
            var event = new preventable_event_1.PreventableEvent();
            if (show) {
                this.open.emit(event);
            }
            else {
                this.close.emit(event);
            }
            if (event.isDefaultPrevented()) {
                return;
            }
            this._toggle(show);
        },
        enumerable: true,
        configurable: true
    });
    DateRangePopupComponent.prototype.ngOnInit = function () {
        this.dateRangeService.registerPopup(this);
    };
    DateRangePopupComponent.prototype.ngAfterViewInit = function () {
        var _this = this;
        this.calendarSubscriptions.add(this.contentCalendar.changes.subscribe(function (changes) { return _this.calendar = changes.first; }));
        this.calendarSubscriptions.add(this.viewCalendar.changes.subscribe(function (changes) { return _this.calendar = changes.first; }));
        if (util_1.isWindowAvailable()) {
            this.zone.runOutsideAngular(function () {
                return _this.windowBlurSubscription = rxjs_1.fromEvent(window, 'blur').subscribe(_this.handleWindowBlur.bind(_this));
            });
        }
    };
    DateRangePopupComponent.prototype.ngOnDestroy = function () {
        this.destroyPopup();
        this.calendarSubscriptions.unsubscribe();
        if (this.activateSubscription) {
            this.activateSubscription.unsubscribe();
        }
        if (this.blurSubscription) {
            this.blurSubscription.unsubscribe();
            this.focusSubscription.unsubscribe();
        }
        if (this.windowBlurSubscription) {
            this.windowBlurSubscription.unsubscribe();
        }
    };
    /**
     *  Opens the popup component and focuses the calendar.
     */
    DateRangePopupComponent.prototype.activate = function () {
        var _this = this;
        if (this.show === true) {
            return;
        }
        if (this.activateSubscription) {
            this.activateSubscription.unsubscribe();
        }
        this.show = true;
        this.ref.markForCheck();
        this.zone.runOutsideAngular(function () {
            _this.activateSubscription = rxjs_1.merge(_this.contentCalendar.changes, _this.viewCalendar.changes)
                .pipe(operators_1.filter(function (changes) { return changes && changes.first; }), operators_1.map(function (changes) { return changes.first; }))
                .subscribe(function (calendar) { return setTimeout(function () { return calendar.focus(); }); });
        });
    };
    /**
     *  Focuses the calendar (if available).
     */
    DateRangePopupComponent.prototype.focus = function () {
        if (this.calendar) {
            this.calendar.focus();
        }
    };
    /**
     * Checks if a focused element ids placed inside the popup.
     *
     * @return boolean;
     */
    DateRangePopupComponent.prototype.hasActiveContent = function () {
        if (!kendo_angular_common_1.isDocumentAvailable() || !this.popupRef) {
            return false;
        }
        return this.popupRef.popupElement.contains(document.activeElement);
    };
    /**
     * Toggles the visibility of the popup.
     * If you use the `toggle` method to show or hide the popup,
     * the `open` and `close` events do not fire.
     *
     * @param show The state of the popup.
     */
    DateRangePopupComponent.prototype.toggle = function (show) {
        var _this = this;
        this.resolvedPromise.then(function () {
            _this._toggle((show === undefined) ? !_this.show : show);
        });
    };
    /**
     * @hidden
     *
     * Closes the popup and triggers the `cancel` event.
     */
    DateRangePopupComponent.prototype.cancelPopup = function () {
        this.show = false;
        this.cancel.emit();
    };
    DateRangePopupComponent.prototype.handleWindowBlur = function () {
        var _this = this;
        if (!this.show) {
            return;
        }
        if (kendo_angular_common_1.hasObservers(this.close)) {
            this.zone.run(function () { return _this.show = false; });
        }
        else {
            this.show = false;
        }
    };
    DateRangePopupComponent.prototype.handleMouseLeave = function () {
        this.dateRangeService.setRange(this.dateRangeService.selectionRange);
    };
    DateRangePopupComponent.prototype.handleKeydown = function (event) {
        var _this = this;
        var altKey = event.altKey, keyCode = event.keyCode;
        if (keyCode === kendo_angular_common_1.Keys.Escape || (altKey && keyCode === kendo_angular_common_1.Keys.ArrowUp)) {
            this.zone.run(function () { return _this.cancelPopup(); });
        }
    };
    DateRangePopupComponent.prototype.subscribeFocusBlur = function (calendar) {
        var _this = this;
        if (this.blurSubscription) {
            this.blurSubscription.unsubscribe();
            this.focusSubscription.unsubscribe();
        }
        if (!calendar) {
            return;
        }
        var nativeElement = calendar.element.nativeElement;
        this.blurSubscription = rxjs_1.fromEvent(nativeElement, 'blur').subscribe(function () { return _this.onBlur.emit(); });
        this.focusSubscription = rxjs_1.fromEvent(nativeElement, 'focus').subscribe(function () { return _this.onFocus.emit(); });
    };
    DateRangePopupComponent.prototype.addPopupSubscriptions = function () {
        var _this = this;
        var subscriptions = [];
        for (var _i = 0; _i < arguments.length; _i++) {
            subscriptions[_i] = arguments[_i];
        }
        if (!utils_1.isPresent(this.popupSubscriptions)) {
            this.popupSubscriptions = new rxjs_1.Subscription();
        }
        subscriptions.map(function (s) { return _this.popupSubscriptions.add(s); });
    };
    Object.defineProperty(DateRangePopupComponent.prototype, "_appendTo", {
        get: function () {
            var appendTo = this.appendTo;
            if (!appendTo || appendTo === 'root') {
                return undefined;
            }
            return appendTo === 'component' ? this.container : appendTo;
        },
        enumerable: true,
        configurable: true
    });
    DateRangePopupComponent.prototype._toggle = function (show) {
        var _this = this;
        this._show = show;
        if (this.popupRef) {
            this.destroyPopup();
        }
        if (this._show) {
            var direction = this.rtl ? 'right' : 'left';
            this.popupRef = this.popupService.open({
                anchor: this.anchor,
                anchorAlign: this.anchorAlign || { vertical: 'bottom', horizontal: direction },
                animate: this.animate,
                appendTo: this._appendTo,
                collision: this.collision,
                content: (this.contentTemplate || {}).templateRef || this.defaultTemplate,
                margin: this.margin,
                popupAlign: this.popupAlign || { vertical: 'top', horizontal: direction },
                positionMode: 'absolute'
            });
            var _a = this.popupRef, popupElement_1 = _a.popupElement, popupAnchorViewportLeave = _a.popupAnchorViewportLeave;
            popupElement_1.setAttribute('id', this.popupUID);
            this.addPopupSubscriptions(this.zone.runOutsideAngular(function () { return rxjs_1.fromEvent(popupElement_1, 'keydown').subscribe(_this.handleKeydown.bind(_this)); }), rxjs_1.fromEvent(popupElement_1, 'mouseleave').subscribe(this.handleMouseLeave.bind(this)), popupAnchorViewportLeave.subscribe(function () { return _this.cancelPopup(); }));
        }
    };
    DateRangePopupComponent.prototype.destroyPopup = function () {
        if (utils_1.isPresent(this.popupRef)) {
            this.popupRef.close();
            this.popupRef = null;
        }
        if (utils_1.isPresent(this.popupSubscriptions)) {
            this.popupSubscriptions.unsubscribe();
        }
    };
    tslib_1.__decorate([
        core_1.ViewChild('container', { read: core_1.ViewContainerRef, static: false }),
        tslib_1.__metadata("design:type", core_1.ViewContainerRef)
    ], DateRangePopupComponent.prototype, "container", void 0);
    tslib_1.__decorate([
        core_1.ViewChild('defaultTemplate', { static: false }),
        tslib_1.__metadata("design:type", core_1.TemplateRef)
    ], DateRangePopupComponent.prototype, "defaultTemplate", void 0);
    tslib_1.__decorate([
        core_1.ContentChild(date_range_popup_template_directive_1.DateRangePopupTemplateDirective, { static: false }),
        tslib_1.__metadata("design:type", date_range_popup_template_directive_1.DateRangePopupTemplateDirective)
    ], DateRangePopupComponent.prototype, "contentTemplate", void 0);
    tslib_1.__decorate([
        core_1.ViewChildren(multiview_calendar_component_1.MultiViewCalendarComponent),
        tslib_1.__metadata("design:type", core_1.QueryList)
    ], DateRangePopupComponent.prototype, "viewCalendar", void 0);
    tslib_1.__decorate([
        core_1.ContentChildren(multiview_calendar_component_1.MultiViewCalendarComponent),
        tslib_1.__metadata("design:type", core_1.QueryList)
    ], DateRangePopupComponent.prototype, "contentCalendar", void 0);
    tslib_1.__decorate([
        core_1.Input(),
        tslib_1.__metadata("design:type", Object)
    ], DateRangePopupComponent.prototype, "animate", void 0);
    tslib_1.__decorate([
        core_1.Input(),
        tslib_1.__metadata("design:type", core_1.ElementRef)
    ], DateRangePopupComponent.prototype, "anchor", void 0);
    tslib_1.__decorate([
        core_1.Input(),
        tslib_1.__metadata("design:type", Object)
    ], DateRangePopupComponent.prototype, "anchorAlign", void 0);
    tslib_1.__decorate([
        core_1.Input(),
        tslib_1.__metadata("design:type", Object)
    ], DateRangePopupComponent.prototype, "appendTo", void 0);
    tslib_1.__decorate([
        core_1.Input(),
        tslib_1.__metadata("design:type", Object)
    ], DateRangePopupComponent.prototype, "collision", void 0);
    tslib_1.__decorate([
        core_1.Input(),
        tslib_1.__metadata("design:type", Object)
    ], DateRangePopupComponent.prototype, "popupAlign", void 0);
    tslib_1.__decorate([
        core_1.Input(),
        tslib_1.__metadata("design:type", Object)
    ], DateRangePopupComponent.prototype, "margin", void 0);
    tslib_1.__decorate([
        core_1.Output(),
        tslib_1.__metadata("design:type", core_1.EventEmitter)
    ], DateRangePopupComponent.prototype, "open", void 0);
    tslib_1.__decorate([
        core_1.Output(),
        tslib_1.__metadata("design:type", core_1.EventEmitter)
    ], DateRangePopupComponent.prototype, "close", void 0);
    tslib_1.__decorate([
        core_1.Output('blur'),
        tslib_1.__metadata("design:type", core_1.EventEmitter)
    ], DateRangePopupComponent.prototype, "onBlur", void 0);
    tslib_1.__decorate([
        core_1.Output('focus'),
        tslib_1.__metadata("design:type", core_1.EventEmitter)
    ], DateRangePopupComponent.prototype, "onFocus", void 0);
    tslib_1.__decorate([
        core_1.Output(),
        tslib_1.__metadata("design:type", core_1.EventEmitter)
    ], DateRangePopupComponent.prototype, "cancel", void 0);
    DateRangePopupComponent = tslib_1.__decorate([
        core_1.Component({
            exportAs: 'kendo-daterange-popup',
            selector: 'kendo-daterange-popup',
            template: "\n        <ng-container #container></ng-container>\n        <ng-template #defaultTemplate>\n            <kendo-multiviewcalendar kendoDateRangeSelection></kendo-multiviewcalendar>\n        </ng-template>\n    "
        }),
        tslib_1.__param(4, core_1.Optional()), tslib_1.__param(4, core_1.Inject(kendo_angular_l10n_1.RTL)),
        tslib_1.__metadata("design:paramtypes", [kendo_angular_popup_1.PopupService,
            date_range_service_1.DateRangeService,
            core_1.ChangeDetectorRef,
            core_1.NgZone, Boolean])
    ], DateRangePopupComponent);
    return DateRangePopupComponent;
}());
exports.DateRangePopupComponent = DateRangePopupComponent;
